'use client';
import React, { useState, useEffect } from 'react';
import { Shield, Lock, Eye, EyeOff } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { supabase } from '@/lib/supabase/client';

interface LockScreenProps {
  onUnlock: () => void;
  userName?: string;
  userRole?: string;
  userEmail?: string;
}

export default function LockScreen({ onUnlock, userName = 'Gebruiker', userRole = 'Admin', userEmail }: LockScreenProps) {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [time, setTime] = useState('');
  const [date, setDate] = useState('');
  const [shaking, setShaking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const update = () => {
      const now = new Date();
      setTime(now.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', hour12: false }));
      setDate(now.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }));
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length === 0 || isLoading) return;

    if (!userEmail) {
      setError('Geen gebruikersaccount gevonden. Log opnieuw in.');
      setShaking(true);
      setTimeout(() => setShaking(false), 500);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: userEmail,
        password,
      });

      if (authError) {
        setError('Onjuist wachtwoord. Probeer opnieuw.');
        setShaking(true);
        setTimeout(() => setShaking(false), 500);
        setPassword('');
      } else {
        onUnlock();
      }
    } catch {
      setError('Er is een fout opgetreden. Probeer opnieuw.');
      setShaking(true);
      setTimeout(() => setShaking(false), 500);
      setPassword('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleUnlock(e as unknown as React.FormEvent);
  };

  return (
    <div
      className="wt-lock-root fixed inset-0 z-[9999] flex flex-col items-center justify-center select-none"
      style={{
        background: 'linear-gradient(135deg, #040810 0%, #070c14 40%, #0a1020 100%)',
      }}
    >
      {/* Background grid */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(rgba(14,165,233,0.025) 1px, transparent 1px),
            linear-gradient(90deg, rgba(14,165,233,0.025) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />

      {/* Radial glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 60% 60% at 50% 50%, rgba(14,165,233,0.05) 0%, transparent 70%)',
        }}
      />

      {/* Top brand */}
      <div className="absolute top-8 left-1/2 -translate-x-1/2 flex items-center gap-2 opacity-40">
        <AppLogo size={20} />
        <span className="font-mono text-xs font-bold tracking-widest text-wt-primary uppercase">WATCHTOWER</span>
      </div>

      {/* Lock icon indicator */}
      <div className="absolute top-8 right-8 flex items-center gap-2 opacity-30">
        <Lock size={14} className="text-wt-muted" />
        <span className="text-xs text-wt-muted font-mono tracking-widest uppercase">Vergrendeld</span>
      </div>

      {/* Main lock panel */}
      <div className="relative z-10 flex flex-col items-center gap-6">
        {/* Clock */}
        <div className="text-center mb-2">
          <div
            className="font-mono text-7xl font-light tabular-nums tracking-widest mb-2"
            style={{ color: '#e2eaf4', textShadow: '0 0 40px rgba(14,165,233,0.2)' }}
          >
            {time || '00:00'}
          </div>
          <div className="text-sm text-wt-muted capitalize tracking-wide">{date}</div>
        </div>

        {/* User avatar + name */}
        <div className="flex flex-col items-center gap-3">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center border-2"
            style={{
              background: 'linear-gradient(135deg, rgba(14,165,233,0.15) 0%, rgba(14,165,233,0.05) 100%)',
              borderColor: 'rgba(14,165,233,0.3)',
              boxShadow: '0 0 30px rgba(14,165,233,0.15)',
            }}
          >
            <Shield size={36} className="text-wt-primary" style={{ filter: 'drop-shadow(0 0 8px rgba(14,165,233,0.5))' }} />
          </div>
          <div className="text-center">
            <p className="text-base font-semibold text-wt-text">{userName}</p>
            <p className="text-xs text-wt-muted mt-0.5">{userRole}</p>
          </div>
        </div>

        {/* Password form */}
        <form
          onSubmit={handleUnlock}
          className={`flex flex-col items-center gap-3 ${shaking ? 'animate-shake' : ''}`}
          style={{ width: '280px' }}
        >
          <div className="relative w-full">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => { setPassword(e.target.value); setError(''); }}
              onKeyDown={handleKeyDown}
              placeholder="Voer wachtwoord in"
              autoFocus
              disabled={isLoading}
              className="w-full px-4 py-3 pr-10 rounded-xl text-sm text-wt-text placeholder-wt-muted/50 outline-none transition-all duration-200 font-mono tracking-widest disabled:opacity-60"
              style={{
                background: 'rgba(14,165,233,0.06)',
                border: `1px solid ${error ? 'rgba(239,68,68,0.5)' : 'rgba(14,165,233,0.2)'}`,
                boxShadow: error ? '0 0 0 1px rgba(239,68,68,0.2)' : 'none',
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(p => !p)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-wt-muted hover:text-wt-text transition-colors"
            >
              {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>

          {error && (
            <p className="text-xs text-red-400 text-center">{error}</p>
          )}

          <button
            type="submit"
            disabled={password.length === 0 || isLoading}
            className="w-full py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{
              background: password.length > 0 && !isLoading ? 'linear-gradient(135deg, rgba(14,165,233,0.3) 0%, rgba(14,165,233,0.15) 100%)' : 'rgba(14,165,233,0.08)',
              border: '1px solid rgba(14,165,233,0.3)',
              color: '#0ea5e9',
              boxShadow: password.length > 0 && !isLoading ? '0 0 20px rgba(14,165,233,0.15)' : 'none',
            }}
          >
            {isLoading ? (
              <>
                <span className="w-3.5 h-3.5 border-2 border-wt-primary/40 border-t-wt-primary rounded-full animate-spin" />
                Verifiëren...
              </>
            ) : (
              'Ontgrendelen'
            )}
          </button>
        </form>

        <p className="text-xs text-wt-muted/40 tracking-widest uppercase mt-2">
          WATCHTOWER Event OS · Vergrendeld
        </p>
      </div>

      <style jsx>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-6px); }
          80% { transform: translateX(6px); }
        }
        .animate-shake {
          animation: shake 0.4s ease-in-out;
        }
      `}</style>
    </div>
  );
}
